import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import Navbar from "@/components/Navbar";
import { AuthProvider } from "@/context/AuthContext";
import { RouteSeo } from "@/components/Seo";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import Landing from "@/pages/Landing";
import Dashboard from "@/pages/Dashboard";
import NewStudy from "@/pages/NewStudy";
import StudyEditor from "@/pages/StudyEditor";
import Login from "@/pages/Login";
import Clients from "@/pages/Clients";
import Invoices from "@/pages/Invoices";
import Settings from "@/pages/Settings";
import Profile from "@/pages/Profile";
import LenderContacts from "@/pages/LenderContacts";
import Revenue from "@/pages/Revenue";
import Reports from "@/pages/Reports";
import WebsitePages from "@/pages/WebsitePages";
import SitePage from "@/pages/SitePage";
import SiteIndex from "@/pages/SiteIndex";
import Workload from "@/pages/Workload";
import PayPage from "@/pages/PayPage";
import DepositPage from "@/pages/DepositPage";
import PortalLogin from "@/pages/PortalLogin";
import Portal from "@/pages/Portal";
import PortalStudy from "@/pages/PortalStudy";
import Testimonials from "@/pages/Testimonials";
import PortalPreview from "@/pages/PortalPreview";
import Compare from "@/pages/Compare";
import FeasibilityStudies from "@/pages/FeasibilityStudies";
import StatusPage from "@/pages/StatusPage";
import Partners from "@/pages/Partners";
import ReferPage from "@/pages/ReferPage";
import PartnerDashboard from "@/pages/PartnerDashboard";
import InvitedRequest from "@/pages/InvitedRequest";
import CompareNew from "@/pages/CompareNew";
import CompareView from "@/pages/CompareView";
import PortalCompare from "@/pages/PortalCompare";
import { StaffImpersonationBanner, PortalImpersonationBanner } from "@/components/ImpersonationBanner";

const P = ({ children, admin }) => <ProtectedRoute admin={admin}>{children}</ProtectedRoute>;

const Shell = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => { if (!hash) window.scrollTo(0, 0); }, [pathname]); // eslint-disable-line react-hooks/exhaustive-deps
  const bare = pathname === "/login" || pathname.startsWith("/pay/") || pathname.startsWith("/deposit/") || pathname.startsWith("/status/") || pathname.startsWith("/partner/") || pathname.startsWith("/portal");
  return (
    <>
      {!bare && <StaffImpersonationBanner />}
      {pathname.startsWith("/portal") && pathname !== "/portal/login" && <PortalImpersonationBanner />}
      {!bare && <Navbar />}
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/pay/:token" element={<PayPage />} />
        <Route path="/deposit/:token" element={<DepositPage />} />
        <Route path="/portal/login" element={<PortalLogin />} />
        <Route path="/portal" element={<Portal />} />
        <Route path="/portal/studies/:id" element={<PortalStudy />} />
        <Route path="/portal/studies/:id/preview" element={<PortalPreview />} />
        <Route path="/portal/compare/:id" element={<PortalCompare />} />
        <Route path="/" element={<Landing />} />
        <Route path="/feasibility-studies" element={<FeasibilityStudies />} />
        <Route path="/locations" element={<SiteIndex kind="city" />} />
        <Route path="/locations/:slug" element={<SitePage kind="city" />} />
        <Route path="/locations/:citySlug/:slug" element={<SitePage kind="combo" />} />
        <Route path="/industries" element={<SiteIndex kind="industry" />} />
        <Route path="/industries/:slug" element={<SitePage kind="industry" />} />
        <Route path="/resources" element={<SiteIndex kind="article" />} />
        <Route path="/resources/:slug" element={<SitePage kind="article" />} />
        <Route path="/refer/:code" element={<ReferPage />} />
        <Route path="/request/:token" element={<InvitedRequest />} />
        <Route path="/status/:token" element={<StatusPage />} />
        <Route path="/partner/:token" element={<PartnerDashboard />} />
        <Route path="/partners" element={<P><Partners /></P>} />
        <Route path="/studies" element={<P><Dashboard /></P>} />
        <Route path="/new" element={<P><NewStudy /></P>} />
        <Route path="/studies/:id" element={<P><StudyEditor /></P>} />
        <Route path="/compare" element={<P><Compare /></P>} />
        <Route path="/compare/new" element={<P><CompareNew /></P>} />
        <Route path="/compare/:id" element={<P><CompareView /></P>} />
        <Route path="/clients" element={<P><Clients /></P>} />
        <Route path="/invoices" element={<P><Invoices /></P>} />
        <Route path="/workload" element={<P><Workload /></P>} />
        <Route path="/testimonials" element={<P><Testimonials /></P>} />
        <Route path="/revenue" element={<P><Revenue /></P>} />
        <Route path="/reports" element={<P><Reports /></P>} />
        <Route path="/website" element={<P><WebsitePages /></P>} />
        <Route path="/settings" element={<P admin><Settings /></P>} />
        <Route path="/profile" element={<P><Profile /></P>} />
        <Route path="/lender-contacts" element={<P><LenderContacts /></P>} />
      </Routes>
    </>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <RouteSeo />
          <Shell />
        </AuthProvider>
      </BrowserRouter>
      <div className="no-print"><Toaster theme="dark" position="bottom-right" /></div>
    </div>
  );
}

export default App;
